import { Controller } from "@hotwired/stimulus"

const SIZE = 9
const CENTER = { x: 4, y: 4 }
const DIRECTIONS = [
  [1, 0],
  [-1, 0],
  [0, 1],
  [0, -1],
  [1, 1],
  [1, -1],
  [-1, 1],
  [-1, -1]
]
const ORTHOGONAL = [
  [1, 0],
  [-1, 0],
  [0, 1],
  [0, -1]
]

const PLAYER_META = {
  red: { label: "Rouge", color: "#ef4444", chip: "bg-red-500" },
  blue: { label: "Bleu", color: "#3b82f6", chip: "bg-blue-500" },
  yellow: { label: "Jaune", color: "#eab308", chip: "bg-yellow-400" },
  green: { label: "Vert", color: "#22c55e", chip: "bg-green-500" }
}

const TYPE_LABEL = {
  chief: "C",
  assassin: "A",
  reporter: "R",
  provocateur: "P",
  necromobile: "N",
  militant: "M"
}

export default class extends Controller {
  static targets = ["board", "turn", "center", "status", "players", "log"]

  connect() {
    document.body.classList.add("djambi-page")
    this.restart()
  }

  disconnect() {
    document.body.classList.remove("djambi-page")
  }

  restart() {
    this.turnOrder = ["red", "blue", "yellow", "green"]
    this.normalTurnCursor = 0
    this.currentPlayer = this.turnOrder[0]
    this.pendingBonusTurns = []
    this.winner = null
    this.draw = false

    this.players = {
      red: { alive: true, elimination: null },
      blue: { alive: true, elimination: null },
      yellow: { alive: true, elimination: null },
      green: { alive: true, elimination: null }
    }

    this.board = Array.from({ length: SIZE }, () => Array(SIZE).fill(null))
    this.pieces = {}
    this.selectedId = null
    this.legalMoves = []
    this.pendingAction = null
    this.logs = []

    this.setupInitialPosition()
    this.pushLog("Partie initialisee")
    this.ensureCurrentPlayerPlayable()
    this.render()
  }

  onCellClick(event) {
    if (this.winner || this.draw) return

    const x = Number(event.currentTarget.dataset.x)
    const y = Number(event.currentTarget.dataset.y)

    if (this.pendingAction) {
      this.handlePendingClick(x, y)
      return
    }

    const piece = this.pieceAt(x, y)

    if (this.selectedId) {
      const allowed = this.legalMoves.some((move) => move.x === x && move.y === y)
      if (allowed) {
        this.executeMove(this.selectedId, { x, y })
        return
      }

      if (piece && this.isSelectableByCurrentPlayer(piece)) {
        this.selectPiece(piece.id)
        this.render()
        return
      }

      this.clearSelection()
      this.render()
      return
    }

    if (piece && this.isSelectableByCurrentPlayer(piece)) {
      this.selectPiece(piece.id)
      this.render()
    }
  }

  selectPiece(pieceId) {
    this.selectedId = pieceId
    this.legalMoves = this.legalDestinationsForPiece(this.pieces[pieceId])
  }

  clearSelection() {
    this.selectedId = null
    this.legalMoves = []
  }

  executeMove(pieceId, destination) {
    const piece = this.pieces[pieceId]
    if (!piece || !piece.alive) return

    const from = { x: piece.x, y: piece.y }
    const target = this.pieceAt(destination.x, destination.y)
    this.pushLog(`Coup: ${PLAYER_META[this.currentPlayer].label} ${this.describePiece(piece)} ${this.cellCoord(from.x, from.y)} -> ${this.cellCoord(destination.x, destination.y)}`)

    if (piece.type === "provocateur") {
      if (!target || !target.alive || !this.isHostilePieceFor(target, this.currentPlayer)) return
      this.movePieceTo(piece, destination)
      this.pendingAction = {
        kind: "relocateLive",
        moverId: piece.id,
        movedPieceId: target.id,
        options: this.allEmptySquares({ allowCenterForLiveChief: target.type === "chief" })
      }
      this.clearSelection()
      this.pushLog(`${this.describePiece(piece)} provoque ${this.describePiece(target)}`)
      this.render()
      return
    }

    if (piece.type === "necromobile") {
      if (!target || target.alive) return
      this.movePieceTo(piece, destination)
      this.pendingAction = {
        kind: "relocateDead",
        moverId: piece.id,
        movedPieceId: target.id,
        options: this.allEmptySquares({ allowCenterForLiveChief: false, includeCenterForDead: true })
      }
      this.clearSelection()
      this.pushLog(`${this.describePiece(piece)} manipule un cadavre`)
      this.render()
      return
    }

    if (piece.type === "reporter") {
      if (target) return
      this.movePieceTo(piece, destination)
      this.clearSelection()

      const splashTargets = this.reporterTargets(piece)
      if (splashTargets.length > 0) {
        this.pendingAction = {
          kind: "reporterTarget",
          moverId: piece.id,
          options: splashTargets
        }
        this.pushLog(`${this.describePiece(piece)} cherche un scandale`)
        this.render()
        return
      }

      this.finalizeMove(piece, from, destination)
      return
    }

    if (target && target.alive && this.isHostilePieceFor(target, this.currentPlayer)) {
      if (piece.type === "militant" && target.type === "chief" && this.isCenter(target.x, target.y)) {
        this.pushLog("Un militant ne peut pas frapper un chef au centre")
        this.render()
        return
      }

      const targetOwnerBeforeDeath = target.owner
      const targetLabel = this.describePiece(target)

      this.movePieceTo(piece, destination)
      this.placeCorpse(target, from)
      this.pushLog(`${this.describePiece(piece)} elimine ${targetLabel}`)
      this.handleDirectChiefDeath(target, targetOwnerBeforeDeath, this.currentPlayer)

      this.clearSelection()
      this.finalizeMove(piece, from, destination)
      return
    }

    if (target) return

    this.movePieceTo(piece, destination)
    this.clearSelection()
    this.finalizeMove(piece, from, destination)
  }

  finalizeMove(piece, from, destination) {
    this.resolveEncirclements()
    if (this.winner || this.draw) {
      this.render()
      return
    }

    if (!piece.alive) {
      this.render()
      return
    }

    if (!piece.isChief() && this.isCenter(destination.x, destination.y)) {
      const options = this.simpleLeaveCenterOptions(piece)
      if (options.length > 0) {
        this.pendingAction = { kind: "leaveCenter", moverId: piece.id, options }
        this.pushLog(`${this.describePiece(piece)} doit quitter le centre`) 
        this.render()
        return
      }
    }

    this.endTurn(this.currentPlayer)
  }

  handlePendingClick(x, y) {
    const action = this.pendingAction
    const allowed = action.options.some((move) => move.x === x && move.y === y)
    if (!allowed) return

    if (action.kind === "relocateLive") {
      const moved = this.pieces[action.movedPieceId]
      const mover = this.pieces[action.moverId]
      const source = { x: moved.x, y: moved.y }
      this.setBoardCell(source.x, source.y, null)
      moved.x = x
      moved.y = y
      this.setBoardCell(x, y, moved.id)
      this.pendingAction = null
      this.pushLog(`${this.describePiece(moved)} est deplace`) 

      if (mover && mover.alive && !mover.isChief() && this.isCenter(mover.x, mover.y)) {
        const options = this.simpleLeaveCenterOptions(mover)
        if (options.length > 0) {
          this.pendingAction = { kind: "leaveCenter", moverId: mover.id, options }
          this.render()
          return
        }
      }

      this.resolveEncirclements()
      if (!this.winner && !this.draw) this.endTurn(this.currentPlayer)
      this.render()
      return
    }

    if (action.kind === "relocateDead") {
      const moved = this.pieces[action.movedPieceId]
      const mover = this.pieces[action.moverId]
      const source = { x: moved.x, y: moved.y }
      this.setBoardCell(source.x, source.y, null)
      moved.x = x
      moved.y = y
      this.setBoardCell(x, y, moved.id)
      this.pendingAction = null
      this.pushLog("Cadavre repositionne")

      if (mover && mover.alive && !mover.isChief() && this.isCenter(mover.x, mover.y)) {
        const options = this.simpleLeaveCenterOptions(mover)
        if (options.length > 0) {
          this.pendingAction = { kind: "leaveCenter", moverId: mover.id, options }
          this.render()
          return
        }
      }

      this.resolveEncirclements()
      if (!this.winner && !this.draw) this.endTurn(this.currentPlayer)
      this.render()
      return
    }

    if (action.kind === "reporterTarget") {
      const reporter = this.pieces[action.moverId]
      const target = this.pieceAt(x, y)
      if (!reporter || !target || !target.alive) return

      const targetOwnerBeforeDeath = target.owner
      this.placeCorpse(target, { x: target.x, y: target.y })
      this.pendingAction = null
      this.pushLog(`${this.describePiece(reporter)} eclabousse ${this.describePiece(target)}`)
      this.handleDirectChiefDeath(target, targetOwnerBeforeDeath, this.currentPlayer)

      this.resolveEncirclements()
      if (!this.winner && !this.draw) this.endTurn(this.currentPlayer)
      this.render()
      return
    }

    if (action.kind === "leaveCenter") {
      const mover = this.pieces[action.moverId]
      if (!mover || !mover.alive) return
      this.movePieceTo(mover, { x, y })
      this.pendingAction = null
      this.pushLog(`${this.describePiece(mover)} quitte le centre`)

      this.resolveEncirclements()
      if (!this.winner && !this.draw) this.endTurn(this.currentPlayer)
      this.render()
    }
  }

  endTurn(lastPlayer) {
    if (this.winner || this.draw) return

    const centerOwner = this.centerChiefOwner()
    if (centerOwner && centerOwner !== lastPlayer && this.players[centerOwner].alive) {
      this.pendingBonusTurns.push(centerOwner)
    }

    let next = null

    while (this.pendingBonusTurns.length > 0 && !next) {
      const candidate = this.pendingBonusTurns.shift()
      if (this.players[candidate].alive) next = candidate
    }

    if (!next) {
      next = this.nextAlivePlayerInOrder(lastPlayer)
    }

    this.currentPlayer = next
    this.ensureCurrentPlayerPlayable()
    this.render()
  }

  ensureCurrentPlayerPlayable() {
    if (this.winner || this.draw) return

    let tries = 0
    while (tries < this.turnOrder.length) {
      if (!this.players[this.currentPlayer].alive) {
        this.currentPlayer = this.nextAlivePlayerInOrder(this.currentPlayer)
        tries += 1
        continue
      }

      const chief = this.findChief(this.currentPlayer)
      if (chief && chief.alive && this.isChiefEncircledByDeadOrEdges(chief)) {
        this.players[this.currentPlayer].alive = false
        this.players[this.currentPlayer].elimination = "encircled"
        this.pushLog(`${PLAYER_META[this.currentPlayer].label} elimine par encerclement`) 
        this.resolveWinnerState()
        this.currentPlayer = this.nextAlivePlayerInOrder(this.currentPlayer)
        tries += 1
        continue
      }

      return
    }
  }

  resolveWinnerState() {
    const alive = this.turnOrder.filter((color) => this.players[color].alive)
    if (alive.length === 1) {
      this.winner = alive[0]
      this.pushLog(`Victoire: ${PLAYER_META[this.winner].label}`)
      return
    }

    if (alive.length === 0) {
      this.draw = true
      this.pushLog("Match nul")
    }
  }

  resolveEncirclements() {
    let changed = true

    while (changed) {
      changed = false
      for (const color of this.turnOrder) {
        if (!this.players[color].alive) continue
        const chief = this.findChief(color)
        if (!chief || !chief.alive) {
          this.players[color].alive = false
          this.players[color].elimination = "assassinated"
          changed = true
          continue
        }

        if (this.isChiefEncircledByDeadOrEdges(chief)) {
          this.players[color].alive = false
          this.players[color].elimination = "encircled"
          this.pushLog(`${PLAYER_META[color].label} elimine par encerclement`) 
          changed = true
        }
      }
    }

    this.resolveWinnerState()
  }

  handleDirectChiefDeath(targetPiece, targetOwnerBeforeDeath, killerColor) {
    if (!targetPiece || targetPiece.type !== "chief") return

    const victimColor = targetOwnerBeforeDeath
    if (!victimColor || !this.players[victimColor].alive) return

    this.players[victimColor].alive = false
    this.players[victimColor].elimination = "assassinated"
    this.pushLog(`${PLAYER_META[victimColor].label} decapite; ses pieces passent a ${PLAYER_META[killerColor].label}`)

    for (const piece of Object.values(this.pieces)) {
      if (piece.owner === victimColor) piece.owner = killerColor
    }

    this.resolveWinnerState()
  }

  reporterTargets(reporter) {
    const targets = []

    for (const [dx, dy] of ORTHOGONAL) {
      const nx = reporter.x + dx
      const ny = reporter.y + dy
      if (!this.inBounds(nx, ny)) continue
      const piece = this.pieceAt(nx, ny)
      if (!piece || !piece.alive) continue
      if (!this.isHostilePieceFor(piece, this.currentPlayer)) continue
      if (piece.type === "chief" && this.isCenter(piece.x, piece.y)) {
        targets.push({ x: nx, y: ny })
        continue
      }
      targets.push({ x: nx, y: ny })
    }

    return targets
  }

  legalDestinationsForPiece(piece) {
    if (!piece || !piece.alive) return []

    const moves = []
    const maxRange = piece.type === "militant" ? 2 : SIZE

    for (const [dx, dy] of DIRECTIONS) {
      for (let step = 1; step <= maxRange; step += 1) {
        const x = piece.x + dx * step
        const y = piece.y + dy * step
        if (!this.inBounds(x, y)) break

        const occupant = this.pieceAt(x, y)
        const centerSquare = this.isCenter(x, y)

        if (occupant) {
          if (piece.type === "reporter") break

          if (!occupant.alive) break

          if (piece.type === "provocateur") {
            if (this.isHostilePieceFor(occupant, this.controllerForPiece(piece))) moves.push({ x, y })
            break
          }

          if (piece.type === "necromobile") break

          if (!this.isHostilePieceFor(occupant, this.controllerForPiece(piece))) break

          if (piece.type === "militant" && occupant.type === "chief" && centerSquare) break
          if (!piece.isChief() && centerSquare) break

          moves.push({ x, y })
          break
        }

        if (piece.type === "provocateur" || piece.type === "necromobile") {
          continue
        }

        if (!piece.isChief() && centerSquare) {
          continue
        }

        moves.push({ x, y })
      }
    }

    return moves
  }

  simpleLeaveCenterOptions(piece) {
    const maxRange = piece.type === "militant" ? 2 : SIZE
    const options = []

    for (const [dx, dy] of DIRECTIONS) {
      for (let step = 1; step <= maxRange; step += 1) {
        const x = piece.x + dx * step
        const y = piece.y + dy * step
        if (!this.inBounds(x, y)) break
        if (this.isCenter(x, y)) continue

        const occupant = this.pieceAt(x, y)
        if (occupant) break

        options.push({ x, y })
      }
    }

    return options
  }

  allEmptySquares({ allowCenterForLiveChief, includeCenterForDead }) {
    const options = []

    for (let y = 0; y < SIZE; y += 1) {
      for (let x = 0; x < SIZE; x += 1) {
        if (this.pieceAt(x, y)) continue

        if (this.isCenter(x, y) && !allowCenterForLiveChief && !includeCenterForDead) continue

        options.push({ x, y })
      }
    }

    return options
  }

  movePieceTo(piece, destination) {
    this.setBoardCell(piece.x, piece.y, null)
    piece.x = destination.x
    piece.y = destination.y
    this.setBoardCell(destination.x, destination.y, piece.id)
  }

  placeCorpse(piece, destination) {
    if (this.board[piece.y][piece.x] === piece.id) {
      this.setBoardCell(piece.x, piece.y, null)
    }
    piece.alive = false
    piece.x = destination.x
    piece.y = destination.y
    this.setBoardCell(destination.x, destination.y, piece.id)
  }

  centerChiefOwner() {
    const piece = this.pieceAt(CENTER.x, CENTER.y)
    if (!piece || !piece.alive || piece.type !== "chief") return null
    return piece.owner
  }

  controllerForPiece(piece) {
    if (!piece || !piece.alive) return null
    const owner = piece.owner
    const ownerState = this.players[owner]

    if (!ownerState) return null
    if (ownerState.alive) return owner

    if (ownerState.elimination === "encircled") {
      return this.centerChiefOwner()
    }

    return null
  }

  isSelectableByCurrentPlayer(piece) {
    if (!piece || !piece.alive) return false
    return this.controllerForPiece(piece) === this.currentPlayer
  }

  isHostilePieceFor(piece, player) {
    const controller = this.controllerForPiece(piece)
    if (controller === player) return false
    return true
  }

  nextAlivePlayerInOrder(fromColor) {
    const startIndex = this.turnOrder.indexOf(fromColor)

    for (let shift = 1; shift <= this.turnOrder.length; shift += 1) {
      const color = this.turnOrder[(startIndex + shift) % this.turnOrder.length]
      if (this.players[color].alive) return color
    }

    return fromColor
  }

  findChief(color) {
    return Object.values(this.pieces).find((piece) => piece.type === "chief" && piece.originalColor === color)
  }

  setupInitialPosition() {
    this.placeCamp("yellow", [0, 0], [1, 0], [0, 1], [1, 1], [2, 2])
    this.placeCamp("green", [8, 0], [7, 0], [8, 1], [7, 1], [6, 2])
    this.placeCamp("blue", [0, 8], [1, 8], [0, 7], [1, 7], [2, 6])
    this.placeCamp("red", [8, 8], [7, 8], [8, 7], [7, 7], [6, 6])
  }

  placeCamp(color, chiefPos, assassinPos, reporterPos, provocateurPos, necroPos) {
    const baseX = Math.min(chiefPos[0], assassinPos[0], reporterPos[0], provocateurPos[0], necroPos[0])
    const baseY = Math.min(chiefPos[1], assassinPos[1], reporterPos[1], provocateurPos[1], necroPos[1])

    this.addPiece(color, "chief", chiefPos[0], chiefPos[1])
    this.addPiece(color, "assassin", assassinPos[0], assassinPos[1])
    this.addPiece(color, "reporter", reporterPos[0], reporterPos[1])
    this.addPiece(color, "provocateur", provocateurPos[0], provocateurPos[1])
    this.addPiece(color, "necromobile", necroPos[0], necroPos[1])

    for (let y = baseY; y < baseY + 3; y += 1) {
      for (let x = baseX; x < baseX + 3; x += 1) {
        if (this.pieceAt(x, y)) continue
        this.addPiece(color, "militant", x, y)
      }
    }
  }

  addPiece(color, type, x, y) {
    const count = Object.values(this.pieces).filter((piece) => piece.originalColor === color && piece.type === type).length + 1
    const id = `${color}-${type}-${count}`
    const piece = {
      id,
      type,
      owner: color,
      originalColor: color,
      alive: true,
      x,
      y,
      isChief() {
        return this.type === "chief"
      }
    }

    this.pieces[id] = piece
    this.setBoardCell(x, y, id)
  }

  setBoardCell(x, y, value) {
    this.board[y][x] = value
  }

  pieceAt(x, y) {
    if (!this.inBounds(x, y)) return null
    const id = this.board[y][x]
    return id ? this.pieces[id] : null
  }

  inBounds(x, y) {
    return x >= 0 && x < SIZE && y >= 0 && y < SIZE
  }

  isCenter(x, y) {
    return x === CENTER.x && y === CENTER.y
  }

  isChiefEncircledByDeadOrEdges(chief) {
    for (const [dx, dy] of DIRECTIONS) {
      const nx = chief.x + dx
      const ny = chief.y + dy

      if (!this.inBounds(nx, ny)) continue

      const occupant = this.pieceAt(nx, ny)
      if (!occupant) return false
      if (occupant.alive) return false
    }

    return true
  }

  describePiece(piece) {
    return `${TYPE_LABEL[piece.type]}-${PLAYER_META[piece.originalColor].label}`
  }

  cellCoord(x, y) {
    const file = String.fromCharCode(65 + x)
    const rank = String(y + 1)
    return `${file}${rank}`
  }

  pushLog(message) {
    this.logs.unshift(message)
    this.logs = this.logs.slice(0, 60)
  }

  render() {
    this.renderBoard()
    this.renderSidebar()
  }

  renderBoard() {
    this.boardTarget.innerHTML = ""

    for (let y = 0; y < SIZE; y += 1) {
      for (let x = 0; x < SIZE; x += 1) {
        const cell = document.createElement("button")
        cell.type = "button"
        cell.dataset.x = String(x)
        cell.dataset.y = String(y)
        cell.dataset.action = "click->djambi#onCellClick"

        const light = (x + y) % 2 === 0
        const background = light ? "#d4d4d4" : "#b8b8b8"
        cell.className = "relative aspect-square w-full flex items-center justify-center border border-slate-500/50"
        cell.style.background = background

        if (this.isCenter(x, y)) {
          cell.style.boxShadow = "inset 0 0 0 3px #111827"
          cell.style.background = "#111827"
        }

        const coord = document.createElement("span")
        coord.className = "absolute top-0.5 left-1 text-[9px] md:text-[10px] leading-none text-slate-700/90 pointer-events-none"
        if (this.isCenter(x, y)) coord.className = "absolute top-0.5 left-1 text-[9px] md:text-[10px] leading-none text-slate-400 pointer-events-none"
        coord.textContent = this.cellCoord(x, y)
        cell.appendChild(coord)

        const selectedPiece = this.selectedId ? this.pieces[this.selectedId] : null
        if (selectedPiece && selectedPiece.x === x && selectedPiece.y === y) {
          cell.style.outline = "3px solid #f59e0b"
          cell.style.outlineOffset = "-3px"
        }

        const pending = this.pendingAction
        const legal = pending ? pending.options : this.legalMoves
        const isLegal = legal.some((move) => move.x === x && move.y === y)
        if (isLegal) {
          cell.style.outline = "3px solid #38bdf8"
          cell.style.outlineOffset = "-3px"
          const marker = document.createElement("span")
          marker.className = "absolute inset-1 rounded-md border-2 border-sky-300 z-20 pointer-events-none"
          cell.appendChild(marker)
        }

        const piece = this.pieceAt(x, y)
        if (piece) {
          const pawn = document.createElement("div")
          pawn.className = "z-10 w-[86%] h-[86%] rounded-lg flex items-center justify-center text-[10px] md:text-xs font-extrabold"

          if (piece.alive) {
            pawn.style.background = PLAYER_META[piece.originalColor].color
            pawn.style.color = piece.originalColor === "yellow" ? "#111827" : "#f8fafc"
            pawn.style.border = "2px solid rgba(0,0,0,0.25)"
            pawn.textContent = TYPE_LABEL[piece.type]

            if (this.controllerForPiece(piece) === this.currentPlayer) {
              pawn.style.boxShadow = "0 0 0 2px rgba(255,255,255,0.75) inset"
            }
          } else {
            pawn.style.background = "#111827"
            pawn.style.color = PLAYER_META[piece.originalColor].color
            pawn.style.border = `2px dashed ${PLAYER_META[piece.originalColor].color}`
            pawn.textContent = "X"
          }

          cell.appendChild(pawn)

          if (isLegal) {
            const targetBadge = document.createElement("span")
            targetBadge.className = "absolute right-0.5 bottom-0.5 z-30 w-4 h-4 rounded-full bg-sky-500 text-white text-[10px] leading-none flex items-center justify-center border border-slate-100 pointer-events-none"
            targetBadge.textContent = "!"
            cell.appendChild(targetBadge)
          }
        }

        this.boardTarget.appendChild(cell)
      }
    }
  }

  renderSidebar() {
    if (this.winner) {
      this.turnTarget.innerHTML = "<span class='inline-flex items-center rounded-lg border border-slate-600 px-2 py-1 text-xs text-slate-300'>Partie terminee</span>"
      this.statusTarget.textContent = `Victoire ${PLAYER_META[this.winner].label}`
    } else if (this.draw) {
      this.turnTarget.innerHTML = "<span class='inline-flex items-center rounded-lg border border-slate-600 px-2 py-1 text-xs text-slate-300'>Partie terminee</span>"
      this.statusTarget.textContent = "Match nul"
    } else {
      const info = PLAYER_META[this.currentPlayer]
      this.turnTarget.innerHTML = `
        <span class="inline-flex items-center gap-2 rounded-lg px-2.5 py-1.5 text-xs font-bold uppercase tracking-wide text-slate-100"
              style="background:${info.color}; box-shadow: 0 0 0 2px rgba(255,255,255,0.15) inset;">
          <span class="inline-block w-2.5 h-2.5 rounded-full bg-white/90"></span>
          AU TOUR DE ${info.label}
        </span>
      `
      this.statusTarget.textContent = this.pendingAction ? this.pendingAction.kind : "En cours"
    }

    const centerOwner = this.centerChiefOwner()
    this.centerTarget.textContent = centerOwner ? PLAYER_META[centerOwner].label : "Vacant"

    const playersHtml = this.turnOrder.map((color) => {
      const info = PLAYER_META[color]
      const state = this.players[color]
      const aliveCount = Object.values(this.pieces).filter((piece) => piece.alive && this.controllerForPiece(piece) === color).length
      const status = state.alive ? "actif" : `elimine (${state.elimination})`

      return `
        <div class="rounded-lg border border-slate-700 p-2 text-xs">
          <div class="flex items-center gap-2">
            <span class="inline-block w-2.5 h-2.5 rounded-full ${info.chip}"></span>
            <strong>${info.label}</strong>
          </div>
          <div class="mt-1 text-slate-300">${status}</div>
          <div class="text-slate-400">Pieces controlees: ${aliveCount}</div>
        </div>
      `
    }).join("")

    this.playersTarget.innerHTML = playersHtml
    this.logTarget.innerHTML = this.logs.map((entry) => `<div class="py-0.5">- ${entry}</div>`).join("")
  }
}
